import { useEffect, useState } from "react";
import { demoApi } from "@/api/client";
import type { BalanceSheetReport } from "@/api/types";
import { describeReadFailure } from "@/lib/apiFailure";

export type BalanceSheetStatus = "loading" | "ready" | "error";

export interface UseBalanceSheetResult {
  /** Null until the request resolves, or if it failed. */
  sheet: BalanceSheetReport | null;
  status: BalanceSheetStatus;
  error: string | null;
}

/**
 * Fetches the tenant's real balance sheet from GET /api/demo/reports/balance-sheet.
 * ef-002 / ADR 0005 — what replaces the Reports screen's "this schema cannot" state and
 * the Dashboard's hard-coded cash constant.
 *
 * A "ready" result does NOT mean there is a sheet to draw: `sheet.hasOpeningBalances` can
 * be false, which is a successful answer meaning this tenant has posted no balances. The
 * caller must branch on it rather than on `status` alone, or a farm with no openings
 * renders $0.00 net worth — a fabricated figure that looks exactly like a real one.
 *
 * Deliberately a copy of useProfitAndLoss's shape rather than a shared generic: the two
 * differ in that branch, and collapsing them would hide it behind a type parameter.
 */
/**
 * @param reloadKey Change it to force a re-read. Dashboard.tsx passes useEntries'
 *   `ledgerGeneration`, so a booth reset that wipes the ledger re-reads this sheet
 *   instead of leaving Cash on hand showing money from deleted entries.
 */
export function useBalanceSheet(reloadKey: number = 0): UseBalanceSheetResult {
  const [sheet, setSheet] = useState<BalanceSheetReport | null>(null);
  const [status, setStatus] = useState<BalanceSheetStatus>("loading");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const controller = new AbortController();

    async function load(): Promise<void> {
      setStatus("loading");
      setError(null);
      try {
        const result = await demoApi.getBalanceSheet(controller.signal);
        if (cancelled) return;
        setSheet(result);
        setStatus("ready");
      } catch (err) {
        if (cancelled || (err instanceof DOMException && err.name === "AbortError")) return;
        setSheet(null);
        setStatus("error");
        setError(describeReadFailure(err));
      }
    }

    void load();
    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [reloadKey]);

  return { sheet, status, error };
}
